import { useEffect, useRef, useState, useCallback } from "react";
import { visibleInterval } from "@/lib/poll";
import { useNavigate } from "@/lib/useNav";
import client from "@/lib/api";
import { Avatar } from "@/components/shared/UI";
import { MentionTextarea, renderWithMentions } from "@/components/form/MentionTextarea";
import { ChatManageDialog } from "@/components/chat/ChatManageDialog";
import { EmojiPicker, QUICK_REACTIONS } from "@/components/chat/EmojiPicker";
import { useChatUploads, PendingFiles, MessageFiles, MAX_FILES } from "@/components/chat/ChatFiles";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { cn } from "@/lib/utils";
import { convertEmoticons } from "@/lib/emoticons";
import { toast } from "sonner";
import { ArrowLeft, Send, Settings2, ListChecks, Pencil, Trash2, X, Check, Minus, SmilePlus, Smile, ArrowDown, Repeat, Reply, CheckCheck, Paperclip, ImagePlus, PlusCircle, Mail, Megaphone, TrendingUp } from "lucide-react";
import { recurLabel } from "@/lib/format";
import { setChatDraft } from "@/lib/chatDrafts";

const pad = (n) => String(n).padStart(2, "0");
const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// "15:00 Today" / "15:00 Monday" (this week) / "14:34 15 Oct" (older)
function clock(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const t = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const today = dayStart(now);
  const monday = new Date(today); monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  if (dayStart(d).getTime() === today.getTime()) return `${t} Today`;
  if (d >= monday) return `${t} ${d.toLocaleDateString("en-AU", { weekday: "long" })}`;
  return `${t} ${d.toLocaleDateString("en-AU", { day: "numeric", month: "short", ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) })}`;
}

const fullStamp = (iso) => new Date(iso).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

function dayLabel(iso) {
  const d = dayStart(new Date(iso));
  const today = dayStart(new Date());
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return new Date(iso).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

const sameDay = (a, b) => a && b && dayStart(new Date(a)).getTime() === dayStart(new Date(b)).getTime();

export function ChatConversation({ chatId, onBack, embedded = false, onClose, onMinimize, paused = false }) {
  const { user, users } = useAuth();
  const { bump } = useChat();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(null);
  const [manage, setManage] = useState(false);
  const [reactBarFor, setReactBarFor] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [reads, setReads] = useState({});
  const uploads = useChatUploads(chatId);
  const fileInput = useRef(null);
  const [dropOver, setDropOver] = useState(false);
  const photoInput = useRef(null);
  const [showPlus, setShowPlus] = useState(false);
  const composing = text.length > 0;
  const atMax = uploads.pending.length >= MAX_FILES;
  useEffect(() => { uploads.clear(); setReplyTo(null); }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showEmoji, setShowEmoji] = useState(false);
  const [showNewPill, setShowNewPill] = useState(false);
  const [unreadAfter, setUnreadAfter] = useState(undefined);
  const scroller = useRef();
  const prevLen = useRef(0);
  const atBottom = useRef(true);
  const editingRef = useRef(null);
  editingRef.current = editing;
  const [typingNames, setTypingNames] = useState([]);
  const lastTyping = useRef(0);

  const fetchTyping = useCallback(() => {
    client.get(`/chats/${chatId}/typing`).then(({ data }) => setTypingNames(data.names || [])).catch(() => {});
  }, [chatId]);

  const signalTyping = () => {
    const now = Date.now();
    if (now - lastTyping.current > 2000) {
      lastTyping.current = now;
      client.post(`/chats/${chatId}/typing`).catch(() => {});
    }
  };

  const reload = useCallback(async (markRead) => {
    if (editingRef.current) return; // don't refresh while editing a message
    try {
      const { data } = await client.get(`/chats/${chatId}/messages`);
      setMsgs(data);
      client.get(`/chats/${chatId}/reads`).then(({ data: r }) => setReads(r || {})).catch(() => {});
      const grew = data.length > prevLen.current;
      prevLen.current = data.length;
      const last = data[data.length - 1];
      const fromOther = grew && last && last.sender_id !== user?.id;
      if (markRead || fromOther) {
        await client.post(`/chats/${chatId}/read`).catch(() => {});
        bump();
      }
      if (fromOther && !atBottom.current) setShowNewPill(true);
    } catch (e) { /* ignore */ }
  }, [chatId, user, bump]);

  useEffect(() => {
    if (!chatId) return;
    prevLen.current = 0;
    atBottom.current = true;
    setUnreadAfter(undefined);
    setMsgs([]);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.get(`/chats/${chatId}`);
        if (cancelled) return;
        setChat(data);
        setUnreadAfter(data.my_last_read || null);
      } catch (e) { /* ignore */ }
      if (!cancelled) await reload(true);
    })();
    return () => { cancelled = true; };
  }, [chatId, reload]);

  useEffect(() => {
    if (!chatId || paused) return;
    return visibleInterval(() => { if (!editingRef.current) reload(false); fetchTyping(); }, 3000);
  }, [chatId, paused, reload, fetchTyping]);

  useEffect(() => {
    if (atBottom.current && scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [msgs]);

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (atBottom.current) setShowNewPill(false);
  };

  const scrollToBottom = () => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottom.current = true;
    setShowNewPill(false);
  };

  const send = async () => {
    const t = convertEmoticons(text.trim());
    if (uploads.uploading) { toast.message("Still uploading, one moment…"); return false; }
    const fileIds = uploads.ids;
    if (!t && !fileIds.length) return true;
    const rt = replyTo;
    setText("");
    setReplyTo(null);
    uploads.clear();
    atBottom.current = true;
    try { await client.post(`/chats/${chatId}/messages`, { text: t, reply_to_id: rt?.id || null, file_ids: fileIds }); await reload(true); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); setText(t); setReplyTo(rt); return false; }
  };
  const sendRef = useRef(send);
  sendRef.current = send;
  const hasDraft = !!text.trim() || uploads.ids.length > 0;
  useEffect(() => {
    if (!embedded) return;
    setChatDraft(chatId, hasDraft ? {
      preview: text.trim() || "Attachment", send: () => sendRef.current(),
      discard: () => { setText(""); setReplyTo(null); uploads.clear(); setChatDraft(chatId, null); },
    } : null);
  }, [embedded, chatId, hasDraft, text]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => setChatDraft(chatId, null), [chatId]);
  const saveEdit = async () => {
    const t = convertEmoticons((editing?.text || "").trim());
    if (!t) return;
    try { await client.patch(`/chats/messages/${editing.id}`, { text: t }); setEditing(null); reload(false); }
    catch (e) { toast.error("Could not edit"); }
  };
  const del = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await client.delete(`/chats/messages/${m.id}`); reload(false); }
    catch (e) { toast.error("Could not delete"); }
  };
  const react = async (mid, emoji) => {
    setReactBarFor(null);
    try { await client.post(`/chats/messages/${mid}/react`, { text: emoji }); reload(false); }
    catch (e) { toast.error("Could not react"); }
  };

  const jumpTo = (id) => {
    const el = scroller.current?.querySelector(`[data-testid="chat-message-${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    setTimeout(() => setHighlightId((h) => (h === id ? null : h)), 1600);
  };

  if (!chat) return <div className="flex h-full items-center justify-center text-sm text-slate-400">Loading…</div>;
  const others = (chat.members || []).filter((mb) => mb.id !== user?.id);
  const lastMineId = [...msgs].reverse().find((x) => x.sender_id === user?.id && !x.deleted)?.id;
  const isManaged = chat.type === "group" || chat.type === "task";

  return (
    <div className="relative flex h-full w-full min-w-0 flex-col overflow-hidden" data-testid="chat-conversation"
      onDragOver={(e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) { e.preventDefault(); setDropOver(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) setDropOver(false); }}
      onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); setDropOver(false); uploads.add(e.dataTransfer.files); } }}>
      {dropOver && (
        <div className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-sky-400 bg-sky-50/90 text-sky-700" data-testid="chat-drop-overlay">
          <ImagePlus className="h-6 w-6" /><span className="text-sm font-semibold">Drop images or files to share</span>
        </div>
      )}
      {/* header */}
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-2.5">
        {onBack && <button onClick={onBack} data-testid="chat-back" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden"><ArrowLeft className="h-4 w-4" /></button>}
        <Avatar name={chat.name} className="h-9 w-9 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900" data-testid="chat-title">{chat.name}</p>
          <p className="truncate text-[11px] text-slate-400">
            {chat.task_recurring && chat.task_recurring !== "None"
              ? <span className="inline-flex items-center gap-0.5 text-sky-500"><Repeat className="h-2.5 w-2.5" />{recurLabel(chat.task_recurring, chat.task_due_date)}</span>
              : chat.type === "direct" ? "Direct message" : `${(chat.members || []).length} members`}
          </p>
        </div>
        {chat.task_id && (
          <button onClick={() => navigate(`/todo?open=${chat.task_id}`)} data-testid="chat-task-link"
            className="flex items-center gap-1 rounded-lg bg-violet-50 px-2 py-1 text-xs font-semibold text-violet-700 hover:bg-violet-100">
            <ListChecks className="h-3.5 w-3.5" />View task
          </button>
        )}
        {chat.opportunity_id && !embedded && (
          <button onClick={() => navigate(`/opportunities/${chat.opportunity_id}`)} data-testid="chat-opportunity-link"
            className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
            <TrendingUp className="h-3.5 w-3.5" />View opportunity
          </button>
        )}
        {chat.campaign_id && !embedded && (
          <button onClick={() => navigate(`/campaigns/${chat.campaign_kind}?open=${chat.campaign_id}`)} data-testid="chat-campaign-link"
            className="flex items-center gap-1 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100">
            {chat.campaign_kind === "edm" ? <Mail className="h-3.5 w-3.5" /> : <Megaphone className="h-3.5 w-3.5" />}View campaign
          </button>
        )}
        {isManaged && (
          <button onClick={() => setManage(true)} data-testid="chat-manage-button" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Settings2 className="h-4 w-4" /></button>
        )}
        {onMinimize && (
          <button onClick={onMinimize} data-testid="chat-window-minimize" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Minus className="h-4 w-4" /></button>
        )}
        {onClose && (
          <button onClick={onClose} data-testid="chat-window-close" className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><X className="h-4 w-4" /></button>
        )}
      </div>

      {/* messages */}
      <div className="relative flex min-h-0 flex-1 flex-col">
      <div ref={scroller} onScroll={onScroll} className="min-w-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden bg-slate-50/60 px-3 py-3" data-testid="chat-messages">
        {msgs.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Say hello 👋</p>
        ) : msgs.map((m, i) => {
          const mine = m.sender_id === user?.id;
          const prev = msgs[i - 1];
          const showName = !mine && chat.type !== "direct" && (!prev || prev.sender_id !== m.sender_id);
          const isFirstUnread = unreadAfter !== undefined && !mine && m.created_at > (unreadAfter || "")
            && (!prev || prev.sender_id === user?.id || prev.created_at <= (unreadAfter || ""));
          return (
            <div key={m.id} className={cn("group flex flex-col rounded-lg transition-colors duration-500", mine ? "items-end" : "items-start", highlightId === m.id && "bg-amber-100/70")} data-testid={`chat-message-${m.id}`}>
              {!sameDay(prev?.created_at, m.created_at) && (
                <div className="my-2 flex w-full items-center gap-2" data-testid={`chat-day-divider-${m.id}`}>
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="rounded-full bg-white px-2.5 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">{dayLabel(m.created_at)}</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
              )}
              {isFirstUnread && (
                <div className="my-1.5 flex w-full items-center gap-2" data-testid="chat-unread-divider">
                  <span className="h-px flex-1 bg-sky-200" />
                  <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-600">New</span>
                  <span className="h-px flex-1 bg-sky-200" />
                </div>
              )}
              {showName && <span className="mb-0.5 ml-1 text-[11px] font-semibold text-slate-500">{m.sender_name}</span>}
              <div className={cn("relative flex min-w-0 max-w-[85%] items-end gap-1.5", mine && "flex-row-reverse")}>
                {editing?.id === m.id ? (
                  <div className="flex items-end gap-1.5">
                    <textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} rows={2} data-testid="chat-edit-input"
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); saveEdit(); } }}
                      className="w-56 resize-none rounded-lg border border-sky-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-100" />
                    <button onClick={saveEdit} data-testid="chat-edit-save" className="rounded-full bg-emerald-500 p-1.5 text-white hover:bg-emerald-600"><Check className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEditing(null)} className="rounded-full bg-slate-200 p-1.5 text-slate-600 hover:bg-slate-300"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <div className={cn("min-w-0 w-fit max-w-full rounded-2xl px-3 py-1.5 text-sm",
                    m.deleted ? "bg-slate-100 italic text-slate-400" : mine ? "bg-sky-600 text-white" : "bg-white text-slate-800 shadow-sm ring-1 ring-slate-100")}>
                    {m.reply_to && !m.deleted && (
                      <button type="button" onClick={() => jumpTo(m.reply_to.id)} data-testid={`chat-reply-quote-${m.id}`}
                        className={cn("mb-1 block w-full min-w-0 rounded-lg border-l-[3px] px-2 py-1 text-left text-[11px] leading-4",
                          mine ? "border-white/70 bg-white/15 text-sky-50 hover:bg-white/25" : "border-sky-400 bg-slate-50 text-slate-500 hover:bg-slate-100")}>
                        {m.reply_to.deleted ? <span className="italic">Original message deleted</span> : (
                          <>
                            <span className={cn("block font-semibold", mine ? "text-white" : "text-sky-700")}>{m.reply_to.sender_id === user?.id ? "You" : m.reply_to.sender_name || "Someone"}</span>
                            <span className="line-clamp-2 [overflow-wrap:anywhere]">{m.reply_to.text}</span>
                          </>
                        )}
                      </button>
                    )}
                    {!m.deleted && <MessageFiles files={m.files} mine={mine} />}
                    {(m.deleted || m.text) && <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{m.deleted ? "Message deleted" : renderWithMentions(m.text, users)}</span>}
                  </div>
                )}
                {!m.deleted && editing?.id !== m.id && (
                  <div className={cn("absolute -top-3.5 z-10 flex items-center gap-0.5 rounded-full bg-white px-0.5 opacity-0 shadow-md ring-1 ring-slate-200 transition-opacity group-hover:opacity-100 focus-within:opacity-100", mine ? "right-2" : "left-2", reactBarFor === m.id && "opacity-100")}>
                    <button onClick={() => setReplyTo({ id: m.id, sender_name: mine ? "yourself" : m.sender_name, text: m.text })} title="Reply" data-testid={`chat-message-reply-${m.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Reply className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setReactBarFor(reactBarFor === m.id ? null : m.id)} data-testid={`chat-message-react-${m.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-amber-500"><SmilePlus className="h-3.5 w-3.5" /></button>
                    {mine && (
                      <>
                        <button onClick={() => setEditing({ id: m.id, text: m.text })} data-testid={`chat-message-edit-${m.id}`} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => del(m)} data-testid={`chat-message-delete-${m.id}`} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                      </>
                    )}
                    {reactBarFor === m.id && (
                      <div className={cn("absolute top-full z-20 mt-1 flex gap-0.5 rounded-full border border-slate-200 bg-white px-1.5 py-1 shadow-lg", mine ? "right-0" : "left-0")} data-testid={`react-bar-${m.id}`}>
                        {QUICK_REACTIONS.map((e) => (
                          <button key={e} onClick={() => react(m.id, e)} className="text-lg leading-none transition-transform hover:scale-125">{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!m.deleted && Object.keys(m.reactions || {}).length > 0 && (
                <div className={cn("-mt-0.5 flex flex-wrap gap-1", mine ? "justify-end pr-1" : "justify-start pl-1")}>
                  {Object.entries(m.reactions).map(([emoji, ids]) => (
                    <button key={emoji} onClick={() => react(m.id, emoji)} data-testid={`reaction-${m.id}-${emoji}`}
                      title={(ids || []).map((id) => (users.find((u) => u.id === id) || {}).name || "Someone").join(", ")}
                      className={cn("flex items-center gap-0.5 rounded-full border px-1.5 py-px text-[11px] transition-colors",
                        (ids || []).includes(user?.id) ? "border-sky-300 bg-sky-50 text-sky-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50")}>
                      <span className="text-xs leading-none">{emoji}</span><span>{(ids || []).length}</span>
                    </button>
                  ))}
                </div>
              )}
              <span title={fullStamp(m.created_at)} data-testid={`chat-message-time-${m.id}`} className={cn("mt-0.5 px-1 text-[10px] text-slate-400", mine ? "text-right" : "text-left")}>{clock(m.created_at)}{m.edited && !m.deleted ? " · edited" : ""}</span>
              {m.id === lastMineId && <SeenLine others={others} reads={reads} at={m.created_at} direct={chat.type === "direct"} />}
            </div>
          );
        })}
      </div>
        {showNewPill && (
          <button onClick={scrollToBottom} data-testid="chat-new-messages-pill"
            className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-lg ring-1 ring-sky-700/20 transition hover:bg-sky-700">
            <ArrowDown className="h-3.5 w-3.5" /> New messages
          </button>
        )}
      </div>

      {/* composer */}
      <div className="border-t border-slate-100 p-2.5">
        {typingNames.length > 0 && (
          <div className="mb-1 flex items-center gap-1 px-1 text-[11px] italic text-slate-400" data-testid="chat-typing">
            <span className="flex gap-0.5">
              <span className="h-1 w-1 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
              <span className="h-1 w-1 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
              <span className="h-1 w-1 animate-bounce rounded-full bg-slate-400" />
            </span>
            {typingNames.length === 1 ? `${typingNames[0]} is typing…`
              : typingNames.length === 2 ? `${typingNames[0]} and ${typingNames[1]} are typing…`
              : "Several people are typing…"}
          </div>
        )}
        {replyTo && (
          <div className="mb-1.5 flex items-center gap-2 rounded-lg border-l-[3px] border-sky-400 bg-sky-50/70 px-2.5 py-1.5" data-testid="chat-reply-banner">
            <Reply className="h-3.5 w-3.5 shrink-0 text-sky-600" />
            <div className="min-w-0 flex-1 text-[11px] leading-4">
              <span className="font-semibold text-sky-700">Replying to {replyTo.sender_name || "message"}</span>
              <span className="block truncate text-slate-500">{replyTo.text}</span>
            </div>
            <button onClick={() => setReplyTo(null)} aria-label="Cancel reply" data-testid="chat-reply-cancel" className="rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        <PendingFiles pending={uploads.pending} onRemove={uploads.remove} />
        <div className="flex items-end gap-1" onPaste={(e) => { const fs = Array.from(e.clipboardData?.files || []); if (fs.length) { e.preventDefault(); uploads.add(fs); } }}>
          <input ref={fileInput} type="file" multiple className="hidden" data-testid="chat-file-input"
            accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
            onChange={(e) => { uploads.add(e.target.files); e.target.value = ""; }} />
          <input ref={photoInput} type="file" multiple className="hidden" data-testid="chat-photo-input" accept=".png,.jpg,.jpeg,.gif,.webp"
            onChange={(e) => { uploads.add(e.target.files); e.target.value = ""; }} />
          {composing ? (
            <div className="relative shrink-0">
              <button onClick={() => setShowPlus((v) => !v)} title="More options" data-testid="chat-plus-button"
                className="flex h-9 w-8 items-center justify-center rounded-full text-sky-600 transition-transform hover:bg-sky-50">
                <PlusCircle className={cn("h-5 w-5 transition-transform duration-200", showPlus && "rotate-45")} />
              </button>
              {showPlus && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowPlus(false)} />
                  <div className="absolute bottom-full left-0 z-20 mb-2 w-60 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl animate-in fade-in slide-in-from-bottom-2" data-testid="chat-plus-menu">
                    <button onClick={() => { setShowPlus(false); photoInput.current?.click(); }} disabled={atMax} data-testid="chat-plus-photo"
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ImagePlus className="h-4 w-4 text-sky-600" /> Add a photo</button>
                    <button onClick={() => { setShowPlus(false); fileInput.current?.click(); }} disabled={atMax} data-testid="chat-plus-file"
                      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"><Paperclip className="h-4 w-4 text-sky-600" /> Attach a file up to 10 MB</button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex shrink-0 items-center">
              <button onClick={() => photoInput.current?.click()} disabled={atMax} title="Add a photo" data-testid="chat-photo-button"
                className="flex h-9 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-sky-600 disabled:opacity-40"><ImagePlus className="h-[18px] w-[18px]" /></button>
              <button onClick={() => fileInput.current?.click()} disabled={atMax} title="Attach a file" data-testid="chat-attach-button"
                className="flex h-9 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-sky-600 disabled:opacity-40"><Paperclip className="h-[18px] w-[18px]" /></button>
            </div>
          )}
          <div className="relative flex min-w-0 flex-1 items-end rounded-[18px] bg-slate-100 pr-1 transition-colors focus-within:bg-slate-50 focus-within:ring-2 focus-within:ring-sky-100" data-testid="chat-input-pill">
            <div className="min-w-0 flex-1">
              <MentionTextarea value={text} onChange={(v) => { setText(v); signalTyping(); }} users={users} rows={1} autoGrow maxRows={6} testid="chat-composer" placeholder="Write message..." onEnter={send} menuUp
                className="!block !min-h-0 !resize-none !border-0 !bg-transparent !px-3.5 !py-2 text-sm leading-5 !shadow-none !ring-0 focus:!ring-0" />
            </div>
            <div className="relative shrink-0">
              <button onClick={() => setShowEmoji((v) => !v)} title="Emoji" data-testid="chat-emoji-button"
                className="flex h-9 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:text-amber-500"><Smile className="h-[18px] w-[18px]" /></button>
              {showEmoji && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowEmoji(false)} />
                  <div className="absolute bottom-full right-0 z-20 mb-2">
                    <EmojiPicker onPick={(e) => setText((t) => t + e)} />
                  </div>
                </>
              )}
            </div>
          </div>
          <button onClick={send} disabled={(!text.trim() && !uploads.pending.length) || uploads.uploading} data-testid="chat-send" title="Send"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sky-600 transition-colors hover:bg-sky-50 disabled:text-slate-300 disabled:hover:bg-transparent"><Send className="h-[18px] w-[18px]" /></button>
        </div>
      </div>

      <ChatManageDialog open={manage} chat={chat} onClose={() => setManage(false)} onChanged={(c) => { setChat(c); bump(); }} />
    </div>
  );
}

function SeenLine({ others, reads, at, direct }) {
  const seen = others.filter((o) => reads[o.id] && reads[o.id] >= at);
  if (!others.length) return null;
  let label;
  if (!seen.length) label = "Sent";
  else if (direct) label = `Seen ${clock(reads[seen[0].id])}`;
  else if (seen.length === others.length) label = "Seen by everyone";
  else label = `Seen by ${seen.map((o) => (o.name || "").split(" ")[0]).join(", ")}`;
  return (
    <span className="-mt-0.5 flex items-center gap-1 px-1 text-[10px] text-slate-400" data-testid="chat-seen" title={seen.map((o) => o.name).join(", ")}>
      {seen.length ? <CheckCheck className="h-3 w-3 text-sky-500" /> : <Check className="h-3 w-3" />}
      {label}
    </span>
  );
}
